import React from 'react';
import { solutions } from '../mock';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { CheckCircle2, ArrowRight, Package, Code } from 'lucide-react';

const Solutions = () => {
  const readyMade = solutions.filter(s => s.category === 'Ready-made');
  const custom = solutions.filter(s => s.category === 'Custom');

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-blue-600 via-blue-700 to-purple-700 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-72 h-72 bg-white rounded-full filter blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-white rounded-full filter blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">Our Solutions</h1>
            <p className="text-xl text-blue-100">
              Pre-built solutions and custom development to meet your unique needs
            </p>
          </div>
        </div>
      </section>

      {/* Ready-made Solutions Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl mb-4">
              <Package className="h-8 w-8 text-white" />
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Ready-Made Solutions</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Industry-tested solutions ready for immediate deployment
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {readyMade.map((solution) => (
              <Card key={solution.id} className="border-none shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 bg-gradient-to-br from-white to-blue-50">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{solution.title}</h3>
                  <p className="text-gray-600 mb-6">{solution.description}</p>
                  <div className="space-y-2 mb-6">
                    {solution.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center text-sm">
                        <CheckCircle2 className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                    <span className="text-blue-600 font-semibold">{solution.price}</span>
                    <Link to="/contact">
                      <Button variant="outline" className="border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white">
                        Get Details
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Custom Solutions Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-purple-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl mb-4">
              <Code className="h-8 w-8 text-white" />
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Custom Solutions</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Bespoke solutions designed specifically for your business requirements
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {custom.map((solution) => (
              <Card key={solution.id} className="border-none shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 bg-gradient-to-br from-white to-purple-50">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{solution.title}</h3>
                  <p className="text-gray-600 mb-6">{solution.description}</p>
                  <div className="space-y-2 mb-6">
                    {solution.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center text-sm">
                        <CheckCircle2 className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                    <span className="text-purple-600 font-semibold">{solution.price}</span>
                    <Link to="/contact">
                      <Button variant="outline" className="border-purple-600 text-purple-600 hover:bg-purple-600 hover:text-white">
                        Discuss Project
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">Why Choose Our Solutions</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <Card className="border-none shadow-lg bg-gradient-to-br from-blue-50 to-white">
                <CardContent className="p-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Rapid Deployment</h3>
                  <p className="text-gray-600">
                    Our ready-made solutions can be deployed quickly, minimizing downtime and 
                    accelerating your digital transformation journey.
                  </p>
                </CardContent>
              </Card>
              <Card className="border-none shadow-lg bg-gradient-to-br from-purple-50 to-white">
                <CardContent className="p-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Fully Customizable</h3>
                  <p className="text-gray-600">
                    Every solution can be customized to match your specific workflows, branding, 
                    and business requirements.
                  </p>
                </CardContent>
              </Card>
              <Card className="border-none shadow-lg bg-gradient-to-br from-blue-50 to-white">
                <CardContent className="p-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Proven Track Record</h3>
                  <p className="text-gray-600">
                    All our solutions are battle-tested and have been successfully implemented 
                    across hundreds of businesses.
                  </p>
                </CardContent>
              </Card>
              <Card className="border-none shadow-lg bg-gradient-to-br from-purple-50 to-white">
                <CardContent className="p-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Ongoing Support</h3>
                  <p className="text-gray-600">
                    We provide comprehensive training, documentation, and continuous support 
                    to ensure your success.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Find the Perfect Solution</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto text-blue-100">
            Let's discuss which solution best fits your business needs
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 text-lg px-8 py-6">
              Contact Our Team <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Solutions;